import React, { useEffect, useState } from 'react';
import { api } from './api';
import { useAuth } from '../../context/AuthContext';
import { Modal } from './ClientsPage';

const ROLES = ['admin','senior','junior','manager','client'];
const ROLE_COLOR = { admin:'#A02020', senior:'#C19A6B', junior:'#2A7A5A', manager:'#A371F7', client:'#58A6FF' };
const ROLE_LABEL = { admin:'Admin', senior:'Senior Planner', junior:'Junior Planner', manager:'Manager', client:'Client Viewer' };

const BLANK = { email:'', name:'', password:'', role:'junior', client_id:'' };

export default function UsersPage() {
  const { user: me, isAdmin } = useAuth();
  const [users, setUsers]   = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]   = useState(null);
  const [form, setForm]     = useState(BLANK);
  const [pwdModal, setPwdModal] = useState(null);
  const [newPwd, setNewPwd] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  async function load() {
    const [u, rawC] = await Promise.all([api.getUsers(), fetch('/api/tv/clients/all',{headers:{'Authorization':'Bearer '+localStorage.getItem('plan_token')}}).then(r=>r.json()).catch(()=>[])]);
    const c=Array.isArray(rawC)?rawC:[];
    setUsers(u); setClients(c);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  function openCreate() { setForm(BLANK); setError(''); setModal('new'); }
  function openEdit(u)  { setForm({ email:u.email, name:u.name, password:'', role:u.role, client_id:u.client_id||'' }); setError(''); setModal(u); }

  async function handleSave() {
    setError('');
    if (!form.email || !form.name || !form.role) { setError('Email, name and role are required'); return; }
    if (modal === 'new' && form.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setSaving(true);
    try {
      if (modal === 'new') {
        await api.createUser({ ...form, client_id: form.client_id || null });
      } else {
        await api.updateUser(modal.id, {
          name: form.name, email: form.email, role: form.role,
          client_id: form.client_id || null
        });
      }
      await load();
      setModal(null);
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  }

  async function handleResetPwd() {
    if (newPwd.length < 6) { alert('Min 6 characters'); return; }
    await api.resetPassword(pwdModal.id, newPwd);
    setPwdModal(null); setNewPwd('');
    alert(`Password reset for ${pwdModal.name}`);
  }

  async function handleToggleActive(u) {
    if (u.id === me.id) { alert("You can't deactivate yourself"); return; }
    await api.updateUser(u.id, { active: u.active ? 0 : 1 });
    load();
  }

  if (loading) return <div style={{ color:'#9A8A7A', padding:40 }}>Loading…</div>;

  return (
    <div style={{ padding: '32px 36px', background: '#F5F5F5', minHeight: '100%', fontFamily: "'Inter',sans-serif" }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <h1 style={styles.h1}>Users</h1>
        {isAdmin && <button style={styles.btnPrimary} onClick={openCreate}>+ New User</button>}
      </div>

      <table style={styles.table}>
        <thead>
          <tr>{['Name','Email','Role','Assigned Client','Status',''].map(h => (
            <th key={h} style={styles.th}>{h}</th>
          ))}</tr>
        </thead>
        <tbody>
          {users.map(u => {
            const clientName = clients.find(c=>c.id===u.client_id)?.name;
            return (
              <tr key={u.id} style={{ borderBottom:'1px solid #161B22' }}>
                <td style={styles.td}>
                  <div style={{ fontWeight:600, color:'#0A0A0A' }}>{u.name}</div>
                  {u.id === me.id && <span style={{ fontSize:10, color:'#C19A6B' }}>← You</span>}
                </td>
                <td style={styles.td}><span style={styles.muted}>{u.email}</span></td>
                <td style={styles.td}>
                  <span style={{
                    padding:'2px 8px', borderRadius:10, fontSize:11, fontWeight:700,
                    background: ROLE_COLOR[u.role]+'22', color: ROLE_COLOR[u.role]
                  }}>{ROLE_LABEL[u.role]}</span>
                </td>
                <td style={styles.td}><span style={styles.muted}>{clientName||'—'}</span></td>
                <td style={styles.td}>
                  <span style={{ fontSize:12, color: u.active ? '#2A7A5A' : '#A02020' }}>
                    {u.active ? '● Active' : '○ Inactive'}
                  </span>
                </td>
                <td style={{ ...styles.td, textAlign:'right' }}>
                  {isAdmin && (<>
                    <button style={styles.btnGhost} onClick={() => openEdit(u)}>Edit</button>
                    <button style={{ ...styles.btnGhost, marginLeft:6 }} onClick={() => { setPwdModal(u); setNewPwd(''); }}>
                      Reset pwd
                    </button>
                    {u.id !== me.id && (
                      <button style={{ ...styles.btnGhost, marginLeft:6, color: u.active?'#A02020':'#2A7A5A' }}
                        onClick={() => handleToggleActive(u)}>
                        {u.active ? 'Deactivate' : 'Activate'}
                      </button>
                    )}
                  </>)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Role legend */}
      <div style={{ marginTop:24, display:'flex', gap:12, flexWrap:'wrap' }}>
        {ROLES.map(r => (
          <div key={r} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#9A8A7A' }}>
            <span style={{ width:8, height:8, borderRadius:'50%', background:ROLE_COLOR[r], display:'inline-block' }}/>
            <strong style={{ color:ROLE_COLOR[r] }}>{ROLE_LABEL[r]}</strong>
            <span>—</span>
            <span>{{
              admin:'Full access, user management',
              senior:'Create/edit/approve plans, all clients',
              junior:'Create/edit plans, assigned clients',
              manager:'Review & approve plans, read-only config',
              client:'View own approved plans only'
            }[r]}</span>
          </div>
        ))}
      </div>

      {/* Create / Edit modal */}
      {modal && (
        <Modal title={modal==='new'?'New User':`Edit: ${modal.name}`} onClose={()=>setModal(null)}>
          {[
            { k:'name',  l:'Full Name *',  t:'text',     ph:'e.g. Ion Popescu' },
            { k:'email', l:'Email *',      t:'email',    ph:'email@company.md' },
            ...(modal==='new' ? [{ k:'password', l:'Password *', t:'password', ph:'Min 6 characters' }] : []),
          ].map(f => (
            <label key={f.k} style={{ display:'block', marginBottom:14 }}>
              <div style={styles.label}>{f.l}</div>
              <input type={f.t} value={form[f.k]} onChange={e=>setForm(p=>({...p,[f.k]:e.target.value}))}
                placeholder={f.ph} style={styles.input} />
            </label>
          ))}

          <label style={{ display:'block', marginBottom:14 }}>
            <div style={styles.label}>Role *</div>
            <select value={form.role} onChange={e=>setForm(p=>({...p,role:e.target.value}))} style={styles.input}>
              {ROLES.map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
            </select>
          </label>

          {form.role === 'client' && (
            <label style={{ display:'block', marginBottom:14 }}>
              <div style={styles.label}>Assign to Client</div>
              <select value={form.client_id} onChange={e=>setForm(p=>({...p,client_id:e.target.value}))} style={styles.input}>
                <option value="">— Select client —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
          )}

          {error && <div style={styles.errorBox}>{error}</div>}
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button style={styles.btnGhost} onClick={()=>setModal(null)}>Cancel</button>
            <button style={styles.btnPrimary} onClick={handleSave} disabled={saving}>
              {saving?'Saving…':modal==='new'?'Create User':'Save Changes'}
            </button>
          </div>
        </Modal>
      )}

      {/* Reset password modal */}
      {pwdModal && (
        <Modal title={`Reset password: ${pwdModal.name}`} onClose={()=>setPwdModal(null)}>
          <label style={{ display:'block', marginBottom:20 }}>
            <div style={styles.label}>New Password (min 6 chars)</div>
            <input type="password" value={newPwd} onChange={e=>setNewPwd(e.target.value)}
              placeholder="New password" style={styles.input} autoFocus />
          </label>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button style={styles.btnGhost} onClick={()=>setPwdModal(null)}>Cancel</button>
            <button style={styles.btnPrimary} onClick={handleResetPwd}>Reset Password</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

const styles = {
  h1:       { margin:0, fontSize:22, fontWeight:800, color:'#0A0A0A' },
  muted:    { color:'#9A8A7A', fontSize:13 },
  table:    { width:'100%', borderCollapse:'collapse' },
  th:       { textAlign:'left', padding:'8px 12px', fontSize:11, fontWeight:600, color:'#9A8A7A', textTransform:'uppercase', letterSpacing:'.05em', borderBottom:'1px solid #21262D' },
  td:       { padding:'12px', fontSize:13, color:'#2A2A2A', background:'#F5F5F5' },
  btnPrimary:{ background:'#C19A6B', color:'#F5F5F5', border:'none', borderRadius:6, padding:'8px 14px', fontWeight:700, fontSize:13, cursor:'pointer' },
  btnGhost: { background:'transparent', border:'1px solid #30363D', borderRadius:6, padding:'6px 12px', color:'#9A8A7A', fontSize:12, cursor:'pointer' },
  label:    { fontSize:11, fontWeight:600, color:'#9A8A7A', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:5 },
  input:    { width:'100%', boxSizing:'border-box', background:'#F5F5F5', border:'1px solid #30363D', borderRadius:6, padding:'9px 12px', color:'#0A0A0A', fontSize:13, outline:'none', fontFamily:'inherit' },
  errorBox: { background:'#2D1117', border:'1px solid #6E1A1A', borderRadius:6, padding:'10px 12px', color:'#A02020', fontSize:13, marginBottom:16 },
};
